import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Theme,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { AppRow } from '../../layout/appRow';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { ColorPalette } from '../../styles/constants';
import { ViewMode } from '../../models/preferences';
import { MonthSelect, ProjectsSummary } from '../../models/reports';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import { format } from 'date-fns';
import {
  formatDisplayReportsDate,
  getDateByWeekNumber,
  getEndDateAccording,
  getStartDateAccording,
} from '../../services/dateService';
import { useGetReportsActivitiesQuery } from '../../store/reports/reportsActivitiesService';
import { AppActivityViews } from '../app/appActivityViews';
import { AppViewModeSelector } from '../app/appViewModeSelector';
import { ROLES_ID } from '../../models/employee';
import { useDisplayDate } from '../../hooks/useDisplayDate';
import { useHandleDisplayDate } from '../../hooks/useHandleDisplayDate';
import { getNextDate, getPreviousDate } from '../../services/reportsService';
import { AppActivityTableViews } from '../app/appActivityTableViews';
import { useReportsFilters } from '../../context/reports/useReportsCtx';
import { generateProjectLabel } from '../../services/projectService';
import {
   formatDateOnly,
} from '../../services/dateService';

interface ReportsProjectItemProps {
  data: ProjectsSummary;
  startDateFilter: string;
  endDateFilter: string;
  employeeIdFilter: string | null;
}

const makeStyles = (theme: Theme) => ({
  accordion: {
    width: '100%',
    p: 0.5,
    mb: 2,
    '&:before': {
      display: 'none',
    },
    borderRadius: '5px',
  },
  accordionSummary: {
    '& .MuiAccordionSummary-content': {
      mt: 0,
      mb: 0,
    },
  },
  accordionSummaryInnerContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    height: '40px',
  },
  name: {
    color: ColorPalette.RAISIN_BLACK,
    display: 'inline-flex',
    mr: 2,
  },
  toolboxWrapper: { ml: -1, mt: -1 },
  toolbox: {
    display: 'inline-flex',
  },
  iconBtn: {
    color: ColorPalette.DARK_GRAY,
  },

  workedTime: {
    fontSize: '14px',
    display: 'inline-flex',
    alignItems: 'center',
    color: ColorPalette.DARK_GRAY,
    mr: 2,
  },

  currentPeriodRow: {
    justifyContent: 'space-between',
    mr: 2,
    mb: 1,

    [theme.breakpoints.down('sm')]: {
      justifyContent: 'space-between',
    },
  },
  currentPeriodBox: { display: 'flex', justifyContent: 'flex-start' },
  customPeriodBox: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginBottom: 2,
    marginTop: 3,
  },

  buttonArrow: {
    color: ColorPalette.DARK_GRAY,
  },
  buttonDate: {
    pt: 1.5,
  },
});

export function ReportsProjectItem({
  data,
  startDateFilter,
  endDateFilter,
  employeeIdFilter,
}: ReportsProjectItemProps): JSX.Element {
  const theme = useTheme();

  const styles = makeStyles(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { project, loggedMDs } = data;

  const [expanded, setExpanded] = useState<boolean>(false);
  const [isOpenViewMode, setIsOpenViewMode] = useState<boolean>(true);
  const [viewMode, setViewMode] = useState(ViewMode.OLD_VIEW);
  const [dateActivity, setDateActivity] = useState<Date>(
    new Date(formatDateOnly(startDateFilter))
  );

  const { displayDate } = useDisplayDate({ viewMode, dateActivity, isMobile });

  const filters = useReportsFilters();

  const isCustomDate = useMemo(() => {
    return filters && filters.monthSelect === MonthSelect.CUSTOM;
  }, [filters]);

  const startDate = useMemo(
    () =>
      isCustomDate && viewMode === ViewMode.OLD_VIEW
        ? format(new Date(formatDateOnly(startDateFilter)), 'yyyy-MM-dd')
        : format(getStartDateAccording(viewMode, dateActivity), 'yyyy-MM-dd'),
    [dateActivity, viewMode, isCustomDate, startDateFilter]
  );
  const endDate = useMemo(
    () =>
      isCustomDate && viewMode === ViewMode.OLD_VIEW
        ? format(new Date(formatDateOnly(endDateFilter)), 'yyyy-MM-dd')
        : format(getEndDateAccording(viewMode, dateActivity), 'yyyy-MM-dd'),
    [dateActivity, viewMode, isCustomDate, endDateFilter]
  );

  const startDateFormatted = useMemo(
    () => formatDisplayReportsDate(startDate),
    [startDate]
  );
  const endDateFormatted = useMemo(
    () => formatDisplayReportsDate(endDate),
    [endDate]
  );

  const {
    isLoading,
    isFetching,
    data: activities = [],
  } = useGetReportsActivitiesQuery(
    {
      startDate,
      endDate,
      employeeId: employeeIdFilter,
      projectId: data.project.id,
      roleId: ROLES_ID.PROJECT_MANAGER,
    },
    { skip: !expanded }
  );

  const isLoadingAll = useMemo<boolean>(
    () => isLoading || isFetching,

    [isFetching, isLoading]
  );

  const onDayActivities = useCallback((activityDate: string) => {
    setDateActivity(new Date(formatDateOnly(activityDate)));
    setViewMode(ViewMode.DAY);
  }, []);

  const onWeekActivities = useCallback(
    (weekNumber: number) => {
      setDateActivity(getDateByWeekNumber(weekNumber, dateActivity));
      setViewMode(ViewMode.WORK_WEEK);
    },
    [dateActivity]
  );

  const handleOpenViewMode = useCallback(() => {
    setIsOpenViewMode((prev) => !prev);
  }, []);

  const handleChangeViewMode = useCallback(
    (viewModeArg: ViewMode) => setViewMode(viewModeArg),
    []
  );

  const { handleDisplayDate } = useHandleDisplayDate({
    viewMode,
    handleChangeViewMode,
  });

  const handlePreviousDate = useCallback(() => {
    setDateActivity(getPreviousDate(dateActivity, viewMode));
  }, [dateActivity, setDateActivity, viewMode]);

  const handleNextDate = useCallback(() => {
    setDateActivity(getNextDate(dateActivity, viewMode));
  }, [dateActivity, setDateActivity, viewMode]);

  const onExpand = useCallback(() => {
    if (expanded) {
      setIsOpenViewMode(false);
    }
    setExpanded((prev) => !prev);
  }, [expanded]);

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={onExpand}
        sx={styles.accordion}
        disableGutters
      >
        <AccordionSummary
          expandIcon={
            <IconButton>
              <ExpandMoreRoundedIcon />
            </IconButton>
          }
          aria-controls="panel1bh-content"
          id="panel1bh-header"
          sx={styles.accordionSummary}
        >
          <Box sx={styles.accordionSummaryInnerContainer}>
            <Box sx={styles.container}>
              <Typography sx={styles.name}>
                {generateProjectLabel(project.name, project.code)}
              </Typography>
            </Box>

            <Typography sx={styles.workedTime}>{`${loggedMDs} MD`}</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={styles.toolboxWrapper}>
            <Box sx={styles.toolbox}>
              <IconButton onClick={handleOpenViewMode} sx={{ mr: 1 }}>
                <ViewModuleOutlinedIcon sx={{ ...styles.iconBtn }} />
              </IconButton>
              {isOpenViewMode && (
                <AppViewModeSelector
                  viewMode={viewMode}
                  handleChangeViewMode={handleChangeViewMode}
                />
              )}
            </Box>
          </Box>
          <Box>
            <AppRow sx={styles.currentPeriodRow}>
              {isCustomDate ? (
                <Box sx={styles.customPeriodBox}>
                  <Typography variant="h1">
                    {startDateFormatted}&nbsp;-
                  </Typography>
                  <Typography variant="h1">&nbsp;{endDateFormatted}</Typography>
                </Box>
              ) : (
                <Box sx={styles.currentPeriodBox}>
                  <Button sx={styles.buttonArrow} onClick={handlePreviousDate}>
                    <ArrowBackIosRoundedIcon
                      fontSize={isMobile ? 'small' : 'medium'}
                    />
                  </Button>
                  <Button sx={styles.buttonArrow} onClick={handleNextDate}>
                    <ArrowForwardIosRoundedIcon
                      fontSize={isMobile ? 'small' : 'medium'}
                    />
                  </Button>
                  <Button sx={styles.buttonDate} onClick={handleDisplayDate}>
                    <Typography variant="h1">{displayDate}</Typography>
                  </Button>
                </Box>
              )}
            </AppRow>
            {viewMode === ViewMode.OLD_VIEW ? (
              <AppActivityTableViews
                startDate={startDate}
                endDate={endDate}
                activities={activities}
              />
            ) : (
              <AppActivityViews
                activities={activities}
                dateActivity={dateActivity}
                viewMode={viewMode}
                isLoading={isLoadingAll}
                onDayActivities={onDayActivities}
                onWeekActivities={onWeekActivities}
                showOnlyloggedTime
                location={filters?.employeeLocation}
              />
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </>
  );
}
